import { Typography, Button, Box, Grid } from '@material-ui/core'
import React, { Dispatch, useEffect, useState } from 'react'
import { OrganizationsClient, OrganizationRequestDTO, OrganizationResponseDTO } from '../generated/ApiClient'
import { Formik, Form, Field } from 'formik'
import { TextField } from 'formik-material-ui'
import * as Yup from 'yup'
import { useDispatch } from "react-redux";
import { actionCreators as snackbar } from '../store/AlertSnackbarStore'
import { utcToLocal } from '../common/Shared'
import { DateTime } from 'luxon'
import CenteredLoader from '../common/CenteredLoader'
import { History } from 'history'
import { useHistory } from 'react-router'
import { useIsUserInRoles, Admin } from '../hooks/useIsUserInRoles'

const OrgDetailsFormSchema = Yup.object().shape({
    name: Yup.string().max(100).required(),
    agencyNumber: Yup.string().max(6).required(),
})

type OrgDetailsFormProperties = Yup.InferType<typeof OrgDetailsFormSchema>

type OrgDetailsFormProps = {
    externalId: string | undefined
}

async function formSubmitCallback(externalId: string | undefined, name: string, agencyNumber: string, dispatch: Dispatch<any>, setOrgData: React.Dispatch<React.SetStateAction<OrganizationResponseDTO | 'Loading' | undefined>>, history: History<any>) {
    const requestData: OrganizationRequestDTO = { name, agencyNumber };
    try {
        if (externalId) {
            const result = await new OrganizationsClient().update2(externalId, requestData);
            setOrgData(result)
        } else {
            const newOrg = await new OrganizationsClient().create(requestData);
            if (newOrg.externalId) {
                history.push(`/organizations/${newOrg.externalId}`);
            }
        }
        dispatch(snackbar.addMessage({ title: "Successfully updated", message: `${name} details were saved`, alertType: 'success' }))
    }
    catch (error) {
        dispatch(snackbar.addError(error, undefined, 'Error writing to the database'));
    }
}

export default function OrganizationDetailsForm(props: OrgDetailsFormProps) {
    const dispatch = useDispatch()
    const history = useHistory()
    const [orgData, setOrgData] = useState<OrganizationResponseDTO | 'Loading' | undefined>('Loading')
    const isGlobalAdmin = useIsUserInRoles(['GlobalAdmin'])
    const isAdmin = useIsUserInRoles(Admin)

    useEffect(() => {
        const fetchData = async () => {
            try {
                if (props.externalId) {
                    const selectedOrg = await new OrganizationsClient().get3(props.externalId);
                    setOrgData(selectedOrg);
                }
                else
                    setOrgData(undefined);
            }
            catch {
                setOrgData(undefined);
            }
        }
        fetchData();
    }, [props.externalId, setOrgData])
    if (orgData === 'Loading')
        return <CenteredLoader />
    return (
        <>
            <Formik initialValues={{ name: orgData?.name || "", agencyNumber: orgData?.agencyNumber || "" }}
                validationSchema={OrgDetailsFormSchema}
                onSubmit={async (values, _) => formSubmitCallback(props.externalId, values.name, values.agencyNumber, dispatch, setOrgData, history)}>
                {({ submitForm, isSubmitting, touched }) => (
                    <Grid container>
                        <Grid item sm={10}>
                            <Form translate='yes'>
                                <Box my={1.5}>
                                    <Grid container alignItems='center' spacing={1}>
                                        <Grid item>
                                            <Typography paragraph display='inline'><b>Name:</b></Typography>
                                        </Grid>
                                        <Grid item>
                                            {isAdmin ?
                                                <Field component={TextField} name='name' type='text' />
                                                : <Typography>{orgData?.name}</Typography>
                                            }
                                        </Grid>
                                    </Grid>
                                    <Grid container alignItems='center' spacing={1}>
                                        <Grid item>
                                            <Typography paragraph display='inline'><b>Agency Number:</b></Typography>
                                        </Grid>
                                        <Grid item>
                                            {isGlobalAdmin ?
                                                <Field component={TextField} name='agencyNumber' type='text' />
                                                : <Typography>{orgData?.agencyNumber}</Typography>
                                            }
                                        </Grid>
                                    </Grid>
                                    {orgData &&
                                        <>
                                            <Grid container alignItems='center' spacing={1}>
                                                <Grid item>
                                                    <Typography paragraph display='inline'><b>Created Date:</b></Typography>
                                                </Grid>
                                                <Grid item>
                                                    <Typography paragraph display='inline'>{utcToLocal(orgData.created).toLocaleString(DateTime.DATETIME_MED_WITH_SECONDS)}</Typography>
                                                </Grid>
                                            </Grid>
                                            <Grid container alignItems='center' spacing={1}>
                                                <Grid item>
                                                    <Typography paragraph display='inline'><b>Last Updated:</b></Typography>
                                                </Grid>
                                                <Grid item>
                                                    <Typography paragraph display='inline'>{utcToLocal(orgData.lastUpdated).toLocaleString(DateTime.DATETIME_MED_WITH_SECONDS)}</Typography>
                                                </Grid>
                                            </Grid>
                                        </>}
                                </Box>
                            </Form>
                        </Grid>
                        <Grid container item sm={2} direction='column' spacing={2} alignItems='center'>
                            <Grid item>
                                {isAdmin && <Button variant='contained' color='primary' size='medium' disabled={isSubmitting} onClick={submitForm}>Save Changes</Button>}
                            </Grid>
                        </Grid>
                    </Grid>
                )}
            </Formik>
        </>
    )
}